import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Button, Input, Label } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { toast } from "react-hot-toast";
import { tableOrderService } from "@/api/services/table-order.service";
import { SITE_ID } from "@/assets/meta/meta";

const MenuCategoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isCreate = useMemo(() => id === "0", [id]);

  const [name, setName] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isKioskVisible, setIsKioskVisible] = useState(true);
  const [isWebVisible, setIsWebVisible] = useState(true);
  const [isPosVisible, setIsPosVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isCreate || !id) return;

    const loadDetail = async () => {
      setLoading(true);
      try {
        const response = await tableOrderService.getMenuCategory(Number(id));
        const data = response.data;
        setName(data.name || "");
        setDisplayOrder(data.display_order || 0);
        setIsActive(Boolean(data.is_active));
        setIsKioskVisible(Boolean(data.is_kiosk_visible));
        setIsWebVisible(Boolean(data.is_web_visible));
        setIsPosVisible(Boolean(data.is_pos_visible));
      } catch (error) {
        toast.error("카테고리 정보를 불러오지 못했습니다.");
        navigate("/table-order/menu-categories");
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [id, isCreate, navigate]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("카테고리 이름을 입력해주세요.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...(Number.isFinite(SITE_ID) ? { site: SITE_ID } : {}),
        name: name.trim(),
        display_order: displayOrder,
        is_active: isActive,
        is_kiosk_visible: isKioskVisible,
        is_web_visible: isWebVisible,
        is_pos_visible: isPosVisible,
      };

      if (isCreate) {
        await tableOrderService.createMenuCategory(payload);
        toast.success("카테고리가 생성되었습니다.");
      } else {
        await tableOrderService.updateMenuCategory(Number(id), payload);
        toast.success("카테고리가 수정되었습니다.");
      }

      navigate("/table-order/menu-categories");
    } catch (error) {
      toast.error("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={isCreate ? "카테고리 생성" : "카테고리 수정"} pageTitle="현장주문" />

        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">{isCreate ? "카테고리 생성" : `카테고리 수정 #${id}`}</h5>
            <div className="d-flex gap-2">
              <Button color="light" onClick={() => navigate("/table-order/menu-categories")}>목록</Button>
              <Button color="primary" onClick={handleSubmit} disabled={saving || loading}>
                {saving ? "저장 중..." : "저장"}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <p className="text-muted mb-0">불러오는 중...</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label className="form-label">이름</Label>
                  <Input value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
                </div>
                <div>
                  <Label className="form-label">정렬 순서</Label>
                  <Input
                    type="number"
                    value={displayOrder}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayOrder(Number(e.target.value || 0))}
                  />
                </div>
                <div>
                  <Label className="form-label">활성 여부</Label>
                  <select className="form-select" value={isActive ? "true" : "false"} onChange={(e) => setIsActive(e.target.value === "true")}>
                    <option value="true">활성</option>
                    <option value="false">비활성</option>
                  </select>
                </div>
                <div>
                  <Label className="form-label">웹 노출</Label>
                  <select className="form-select" value={isWebVisible ? "true" : "false"} onChange={(e) => setIsWebVisible(e.target.value === "true")}>
                    <option value="true">노출</option>
                    <option value="false">숨김</option>
                  </select>
                </div>
                <div>
                  <Label className="form-label">키오스크 노출</Label>
                  <select className="form-select" value={isKioskVisible ? "true" : "false"} onChange={(e) => setIsKioskVisible(e.target.value === "true")}>
                    <option value="true">노출</option>
                    <option value="false">숨김</option>
                  </select>
                </div>
                <div>
                  <Label className="form-label">POS 노출</Label>
                  <select className="form-select" value={isPosVisible ? "true" : "false"} onChange={(e) => setIsPosVisible(e.target.value === "true")}>
                    <option value="true">노출</option>
                    <option value="false">숨김</option>
                  </select>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default MenuCategoryForm;
